import {
  presignAfImageUpload,
  confirmAfImageUpload,
} from "@/lib/api/handlers/AfImageHandling";
import { ServerError } from "@/lib/api/errors/ErrorHandler";
import { AfImageValue } from "@/types/AfImage";

/**
 * Client-side allowlist for IMAGE additional-field uploads — mirrors the
 * backend af-images presign validation (master spec 2026-07-10). Keep in
 * sync; the backend rejects anything else at presign with 400, this just
 * lets the admin fail up-front instead of after picking the file.
 */
export const ALLOWED_AF_IMAGE_CONTENT_TYPES = new Set<string>([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

/** Admin-side size cap (bytes) — backend mirror: 10 MB. */
export const MAX_AF_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

export function isAllowedAfImageContentType(
  contentType: string | undefined | null,
): boolean {
  if (!contentType) return false;
  return ALLOWED_AF_IMAGE_CONTENT_TYPES.has(contentType.toLowerCase());
}

/**
 * Discriminated-union result (mirrors attachment-upload.ts UploadResult) so
 * callers can render per-failure-mode UX. `validation` failures never leave
 * the client (wrong type / too big); the three network stages map onto the
 * presign → S3 PUT → confirm chain.
 */
export type AfImageUploadResult =
  | { ok: true; value: AfImageValue }
  | {
      ok: false;
      stage: "validation" | "presign" | "upload" | "confirm";
      /** i18n-ready code for validation failures (wrongType / tooBig);
       *  backend errorCode / transport message otherwise. */
      message?: string;
    };

/**
 * Drives the presign → S3 PUT → confirm chain for one IMAGE additional-field
 * upload. Mirrors attachment-upload.ts `runPresignedUpload` exactly, with the
 * guest/field-scoped af-images endpoints and the image-specific client-side
 * checks (content-type allowlist + 10 MB cap) run before any network call.
 */
export async function uploadAfImagePresigned(
  eventId: string,
  guestId: string,
  fieldName: string,
  file: File,
): Promise<AfImageUploadResult> {
  const contentType = (file.type || "").toLowerCase();
  if (!isAllowedAfImageContentType(contentType)) {
    return { ok: false, stage: "validation", message: "wrongType" };
  }
  if (file.size > MAX_AF_IMAGE_SIZE_BYTES) {
    return { ok: false, stage: "validation", message: "tooBig" };
  }

  const presignResult = await presignAfImageUpload(eventId, guestId, fieldName, {
    fileName: file.name,
    contentType,
    contentLength: file.size,
  });
  if (!presignResult || "isServerError" in presignResult) {
    return {
      ok: false,
      stage: "presign",
      message:
        presignResult && "message" in presignResult
          ? (presignResult as ServerError).message
          : undefined,
    };
  }

  let uploadResponse: Response;
  try {
    uploadResponse = await fetch(presignResult.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": contentType },
    });
  } catch (err) {
    return {
      ok: false,
      stage: "upload",
      message: err instanceof Error ? err.message : "S3 upload failed",
    };
  }
  if (!uploadResponse.ok) {
    return {
      ok: false,
      stage: "upload",
      message: `S3 returned ${uploadResponse.status}`,
    };
  }

  const confirmResult = await confirmAfImageUpload(eventId, guestId, fieldName, {
    s3Key: presignResult.s3Key,
  });
  if (!confirmResult || "isServerError" in confirmResult) {
    return {
      ok: false,
      stage: "confirm",
      message:
        confirmResult && "message" in confirmResult
          ? (confirmResult as ServerError).message
          : undefined,
    };
  }
  return { ok: true, value: confirmResult as AfImageValue };
}
